import { useEffect, useState } from 'react';

import { APICallStatus } from 'src/contentful/product-catalog';

import type { Request as TesseractRequest } from '@atlassian/tesseract-runtime-globals';

import {
	BxpExpressServiceSupportedRegions,
	TesseractRuntimeSupportedRegions,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';

export type UserLocale = string;
export const DEFAULT_COUNTRY_CODE: UserLocale = 'US';
export const LOCALE_CACHE_KEY = 'bxp.currentLocale';

export interface LocationData {
	countryIsoCode: string;
	regionIsoCode: string;
}

export type CurrencyISOCode = 'USD' | 'JPY';
export const COUNTRY_CODE_TO_CURRENCY: Record<UserLocale, CurrencyISOCode> = {
	US: 'USD',
	JP: 'JPY',
	ja: 'JPY',
};

export const getCurrencyFromUserLocale = (userLocale: UserLocale): CurrencyISOCode => {
	// @ts-expect-error legacy
	return COUNTRY_CODE_TO_CURRENCY[userLocale] ?? COUNTRY_CODE_TO_CURRENCY[DEFAULT_COUNTRY_CODE];
};

export async function fetchUserLocaleS2SWithLocationData(
	baseUrl: string,
	request: Request | TesseractRequest,
): Promise<LocationData> {
	if (typeof window === 'undefined') {
		// @ts-expect-error legacy
		const environmentType: string = globalThis.ssrContext?.environmentType;
		const modeEnv = process.env['MODE'];
		const version = process.env['VERSION'];
		const isCI = process.env['CI'] === 'true';
		const nodeEnv = process.env['NODE_ENV'];

		// Call location API in:
		// - Deployed environments (staging, production)
		// - Tests (to use spies/mocks)
		// Skip in all other cases (local dev, CI non-test builds)
		const shouldCallAPI = nodeEnv && ['staging', 'production', 'test'].includes(nodeEnv);
		if (!shouldCallAPI) {
			console.log('[LOCATION API] Local dev mode detected - Skipping /endpoint/location call', {
				environmentType,
				MODE: modeEnv,
				returning: {
					countryIsoCode: DEFAULT_COUNTRY_CODE,
					regionIsoCode: '',
				},
			});
			return {
				countryIsoCode: DEFAULT_COUNTRY_CODE,
				regionIsoCode: '',
			};
		}

		console.log('[LOCATION API] Not in dev mode - Will make /endpoint/location call', {
			environmentType,
			MODE: modeEnv,
			VERSION: version,
			CI: isCI,
		});

		const headers: Record<string, string> = {};

		// @ts-expect-error legacy
		const awsRegion: string = globalThis.ssrContext.awsRegion as TesseractRuntimeSupportedRegions;

		// S2S calls only work if the downstream service exists in the same region that our tesseract runtime is in
		const downStreamServiceExistsInSameTesseractRuntimeRegion: boolean =
			BxpExpressServiceSupportedRegions.has(awsRegion);

		if (!downStreamServiceExistsInSameTesseractRuntimeRegion) {
			console.log(
				'Downstream service does not exist in the same region as current tesseract region. Current tesseract region: ' +
					awsRegion,
			);
			const fallbackLocale = await fetchUserLocale(baseUrl);
			return {
				countryIsoCode: fallbackLocale,
				regionIsoCode: '',
			};
		}

		headers['x-ssr-service-name'] = 'bxp-express';
		// bxp-express service does not have any dev instances so when we're in dev, point to staging
		// Note: This code path should not be reached in dev mode due to early return above
		if (environmentType === 'dev') {
			headers['x-ssr-service-env'] = 'staging';
			headers['x-ssr-service-region'] = 'us-east-1'; // Bxp-express service in staging only lives in us-east-1 or us-west-2
		}

		// @ts-expect-error legacy
		headers['X-Forwarded-For'] = request?.headers['x-forwarded-for'];

		console.log('[LOCATION API] Making fetch call to /location (routes to /endpoint/location)', {
			url: '/location',
			headers: Object.keys(headers),
		});

		return await fetch(`/location`, { headers: headers })
			.then((response) => {
				return response.json();
			})
			.then((json) => {
				return {
					countryIsoCode: (json.countryIsoCode as UserLocale) || DEFAULT_COUNTRY_CODE,
					regionIsoCode: json.regionIsoCode || '',
				};
			})
			.catch(async () => {
				// If there's an error with s2s call - fall back to original fetchUserLocale
				const fallbackLocale = await fetchUserLocale(baseUrl);
				return {
					countryIsoCode: fallbackLocale,
					regionIsoCode: '',
				};
			});
	} else {
		const fallbackLocale = await fetchUserLocale(baseUrl);
		return {
			countryIsoCode: fallbackLocale,
			regionIsoCode: '',
		};
	}
}

export async function fetchUserLocaleS2S(
	baseUrl: string,
	request: Request | TesseractRequest,
): Promise<UserLocale> {
	let localeFromStorage;
	if (typeof window !== 'undefined') {
		localeFromStorage = sessionStorage.getItem(LOCALE_CACHE_KEY) as UserLocale;
	}

	if (localeFromStorage) {
		return Promise.resolve(localeFromStorage);
	}

	const locationData = await fetchUserLocaleS2SWithLocationData(baseUrl, request);
	return locationData.countryIsoCode;
}

export async function fetchUserLocale(baseUrl: string): Promise<UserLocale> {
	let localeFromStorage;
	if (typeof window !== 'undefined') {
		localeFromStorage = sessionStorage.getItem(LOCALE_CACHE_KEY) as UserLocale;
	}

	if (localeFromStorage) {
		return Promise.resolve(localeFromStorage);
	}

	const locationData = await fetchLocationDataWithBaseUrl(baseUrl);
	return locationData.countryIsoCode;
}

export async function fetchLocationDataWithBaseUrl(baseUrl: string): Promise<LocationData> {
	return await fetch(`${baseUrl}/location`)
		.then((response) => {
			return response.json();
		})
		.then((json) => {
			return {
				countryIsoCode: (json.countryIsoCode as UserLocale) || DEFAULT_COUNTRY_CODE,
				regionIsoCode: json.regionIsoCode || '',
			};
		})
		.catch(() => {
			return {
				countryIsoCode: DEFAULT_COUNTRY_CODE,
				regionIsoCode: '',
			};
		});
}

export function useGeoLocation(baseUrl: string) {
	const [status, setStatus] = useState<APICallStatus>('idle');
	const [countryCode, setCountryCode] = useState<UserLocale>();

	useEffect(() => {
		const fetchData = async () => {
			setStatus('fetching');
			const locale = await fetchUserLocale(baseUrl);
			setStatus('complete');
			setCountryCode(locale);
		};
		fetchData();
	}, []);
	return { status, countryCode };
}

const EMEA_COUNTRY_CODES = new Set([
	'AL', // "Albania"
	'DZ', // "Algeria"
	'AD', // "Andorra"
	'AO', // "Angola"
	'AT', // "Austria"
	'BH', // "Bahrain"
	'BY', // "Belarus"
	'BE', // "Belgium"
	'BJ', // "Benin"
	'BA', // "Bosnia and Herzegovina"
	'BW', // "Botswana"
	'BV', // "Bouvet Island"
	'IO', // "British Indian Ocean Territory"
	'BG', // "Bulgaria"
	'BF', // "Burkina Faso"
	'BI', // "Burundi"
	'CM', // "Cameroon"
	'CV', // "Cape Verde"
	'CF', // "Central African Republic"
	'TD', // "Chad"
	'KM', // "Comoros"
	'CG', // "Congo"
	'CI', // "Côte d'Ivoire"
	'HR', // "Croatia"
	'CY', // "Cyprus"
	'CZ', // "Czech Republic"
	'CD', // "Democratic Republic of the Congo"
	'DK', // "Denmark"
	'DJ', // "Djibouti"
	'EG', // "Egypt"
	'GQ', // "Equatorial Guinea"
	'ER', // "Eritrea"
	'EE', // "Estonia"
	'ET', // "Ethiopia"
	'FO', // "Faroe Islands"
	'FI', // "Finland"
	'FR', // "France"
	'FX', // "France (European Territory)"
	'GA', // "Gabon"
	'GM', // "Gambia"
	'DE', // "Germany"
	'GH', // "Ghana"
	'GI', // "Gibraltar"
	'GR', // "Greece"
	'GN', // "Guinea"
	'GW', // "Guinea-Bissau"
	'HU', // "Hungary"
	'IS', // "Iceland"
	'IR', // "Iran"
	'IQ', // "Iraq"
	'IE', // "Ireland"
	'IL', // "Israel"
	'IT', // "Italy"
	'JO', // "Jordan"
	'KE', // "Kenya"
	'KW', // "Kuwait"
	'LV', // "Latvia"
	'LB', // "Lebanon"
	'LS', // "Lesotho"
	'LR', // "Liberia"
	'LY', // "Libya"
	'LI', // "Liechtenstein"
	'LT', // "Lithuania"
	'LU', // "Luxembourg"
	'MK', // "North Macedonia"
	'MG', // "Madagascar"
	'MW', // "Malawi"
	'ML', // "Mali"
	'MT', // "Malta"
	'MR', // "Mauritania"
	'MU', // "Mauritius"
	'YT', // "Mayotte"
	'MD', // "Moldova"
	'MC', // "Monaco"
	'ME', // "Montenegro"
	'MA', // "Morocco"
	'MZ', // "Mozambique"
	'NA', // "Namibia"
	'NL', // "Netherlands"
	'NE', // "Niger"
	'NG', // "Nigeria"
	'NO', // "Norway"
	'OM', // "Oman"
	'EU', // "Other Europe"
	'PL', // "Poland"
	'PT', // "Portugal"
	'QA', // "Qatar"
	'RE', // "Réunion"
	'RO', // "Romania"
	'RU', // "Russia - Russian Federation"
	'RW', // "Rwanda"
	'SM', // "San Marino"
	'SA', // "Saudi Arabia"
	'SN', // "Senegal"
	'RS', // "Serbia"
	'SC', // "Seychelles"
	'SL', // "Sierra Leone"
	'SK', // "Slovakia"
	'SI', // "Slovenia"
	'SO', // "Somalia"
	'ZA', // "South Africa"
	'ES', // "Spain"
	'SH', // "St Helena"
	'SD', // "Sudan"
	'SJ', // "Svalbard"
	'SZ', // "Eswatini (formerly Swaziland)"
	'SE', // "Sweden"
	'CH', // "Switzerland"
	'SY', // "Syria"
	'TZ', // "Tanzania"
	'TG', // "Togo"
	'TN', // "Tunisia"
	'TR', // "Turkey"
	'UG', // "Uganda"
	'AE', // "United Arab Emirates"
	'GB', // "United Kingdom"
	'VA', // "Vatican City"
	'EH', // "Western Sahara"
	'YE', // "Yemen"
	'ZM', // "Zambia"
	'ZW', // "Zimbabwe"
]);

const AMER_COUNTRY_CODES = new Set([
	'AI', // Anguilla
	'AG', // Antigua and Barbuda
	'AR', // Argentina
	'AW', // Aruba
	'BS', // Bahamas
	'BB', // Barbados
	'BZ', // Belize
	'BM', // Bermuda
	'BO', // Bolivia
	'BR', // Brazil
	'CA', // Canada
	'KY', // Cayman Islands
	'CL', // Chile
	'CO', // Colombia
	'CR', // Costa Rica
	'DM', // Dominica
	'DO', // Dominican Republic
	'EC', // Ecuador
	'SV', // El Salvador
	'GF', // French Guiana
	'GL', // Greenland
	'GD', // Grenada
	'GP', // Guadeloupe
	'GT', // Guatemala
	'GY', // Guyana
	'HT', // Haiti
	'HN', // Honduras
	'JM', // Jamaica
	'MQ', // Martinique
	'MX', // Mexico
	'MS', // Montserrat
	'AN', // Netherlands Antilles
	'NI', // Nicaragua
	'PA', // Panama
	'PY', // Paraguay
	'PE', // Peru
	'PR', // Puerto Rico
	'KN', // Saint Kitts and Nevis
	'LC', // Saint Lucia
	'PM', // Saint Pierre and Miquelon
	'VC', // Saint Vincent and the Grenadines
	'GS', // South Georgia and the South Sandwich Islands
	'SR', // Suriname
	'TT', // Trinidad and Tobago
	'TC', // Turks and Caicos Islands
	'UY', // Uruguay
	'US', // USA
	'VE', // Venezuela
	'VG', // Virgin Islands (British)
	'VI', // Virgin Islands (US)
]);

const APAC_COUNTRY_CODES = new Set([
	'AF', // Afghanistan
	'AS', // American Samoa
	'AQ', // Antarctica
	'AU', // Australia
	'BD', // Bangladesh
	'BT', // Bhutan
	'BN', // Brunei
	'KH', // Cambodia
	'CN', // China
	'CK', // Cook Islands
	'FJ', // Fiji
	'PF', // French Polynesia
	'TF', // French Southern Territories
	'GU', // Guam
	'HK', // Hong Kong
	'IN', // India
	'ID', // Indonesia
	'JP', // Japan
	'KZ', // Kazakhstan
	'KR', // Korea (South)
	'KG', // Kyrgyzstan
	'LA', // Laos
	'MO', // Macau
	'MY', // Malaysia
	'MV', // Maldives
	'MH', // Marshall Islands
	'MN', // Mongolia
	'NR', // Nauru
	'NP', // Nepal
	'NC', // New Caledonia
	'NZ', // New Zealand
	'NF', // Norfolk Island
	'MP', // Northern Mariana Islands
	'PK', // Pakistan
	'PG', // Papua New Guinea
	'PH', // Philippines
	'WS', // Samoa
	'SG', // Singapore
	'LK', // Sri Lanka
	'TW', // Taiwan
	'TJ', // Tajikistan
	'TH', // Thailand
	'TL', // Timor-Leste
	'TO', // Tonga
	'TM', // Turkmenistan
	'TV', // Tuvalu
	'UZ', // Uzbekistan
	'VU', // Vanuatu
	'VN', // Vietnam
]);

export type GeoRegion = 'emea' | 'amer' | 'apac' | 'unknown';

export const getRegionFromCountryCode = (countryCode: string | undefined): GeoRegion => {
	if (!countryCode) return 'unknown';
	return EMEA_COUNTRY_CODES.has(countryCode)
		? 'emea'
		: AMER_COUNTRY_CODES.has(countryCode)
			? 'amer'
			: APAC_COUNTRY_CODES.has(countryCode)
				? 'apac'
				: 'unknown';
};
